/**
 * Report schedules — a report sent on a cadence to a list of addresses
 * (roadmap RP-7; migration 825).
 *
 * The row holds the report, its controls and the recipients. The ambient job
 * `report_digest` (supabase/functions/ambient/lib/reportDigest.ts) claims the
 * rows that are due and sends the summary with a link to the live report.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Json } from '../types/database.generated';
import { getCurrentWorkspaceId } from './supabaseSync';
import type { ReportSnapshotConfig } from './reportSnapshotsService';

export type ScheduleCadence = 'daily' | 'weekly' | 'monthly';

export const CADENCE_OPTIONS: ReadonlyArray<{ value: ScheduleCadence; label: string }> = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
];

export interface ReportSchedule {
  id: string;
  workspaceId: string;
  reportKey: string;
  name: string;
  config: ReportSnapshotConfig;
  cadence: ScheduleCadence;
  recipients: string[];
  enabled: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  createdAt: string;
}

type Row = {
  id: string;
  workspace_id: string;
  report_key: string;
  name: string;
  config: unknown;
  cadence: string;
  recipients: string[];
  enabled: boolean;
  next_run_at: string;
  last_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  created_at: string;
};

function scheduleFromRow(r: Row): ReportSchedule {
  const config = (r.config && typeof r.config === 'object' ? r.config : {}) as Partial<ReportSnapshotConfig>;
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    reportKey: r.report_key,
    name: r.name,
    config: {
      search: config.search ?? '',
      rangeLabel: config.rangeLabel ?? 'All time',
      segmentLabel: config.segmentLabel ?? null,
      reportPath: config.reportPath ?? '/prioritization/reports',
      reportName: config.reportName ?? r.report_key,
      savedReportId: config.savedReportId ?? null,
      statement: config.statement ?? null,
    },
    cadence: (r.cadence as ScheduleCadence) ?? 'weekly',
    recipients: r.recipients ?? [],
    enabled: r.enabled,
    nextRunAt: r.next_run_at,
    lastRunAt: r.last_run_at,
    lastStatus: r.last_status,
    lastError: r.last_error,
    createdAt: r.created_at,
  };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a typed list ("a@x.com, b@y.org") into addresses; invalid ones are returned separately. */
export function parseRecipients(text: string): { valid: string[]; invalid: string[] } {
  const parts = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const p of parts) {
    if (EMAIL.test(p)) { if (!valid.includes(p.toLowerCase())) valid.push(p.toLowerCase()); }
    else invalid.push(p);
  }
  return { valid, invalid };
}

export interface CreateScheduleInput {
  reportKey: string;
  name: string;
  config: ReportSnapshotConfig;
  cadence: ScheduleCadence;
  recipients: string[];
}

export async function createReportSchedule(input: CreateScheduleInput): Promise<{ schedule: ReportSchedule | null; error: string | null }> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { schedule: null, error: 'No workspace is open.' };
  if (input.recipients.length === 0) return { schedule: null, error: 'Add at least one address.' };
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('report_schedules')
    .insert({
      workspace_id: workspaceId,
      report_key: input.reportKey,
      name: input.name,
      config: input.config as unknown as NonNullable<Json>,
      cadence: input.cadence,
      recipients: input.recipients,
    })
    .select('*')
    .single();
  if (error || !data) return { schedule: null, error: error?.message ?? 'The schedule was not saved.' };
  return { schedule: scheduleFromRow(data as Row), error: null };
}

export async function listReportSchedules(reportKey?: string): Promise<ReportSchedule[]> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return [];
  const db = getTypedSupabaseClient();
  let q = db.from('report_schedules').select('*').eq('workspace_id', workspaceId).order('created_at', { ascending: false });
  if (reportKey) q = q.eq('report_key', reportKey);
  const { data, error } = await q;
  if (error || !data) return [];
  return (data as Row[]).map(scheduleFromRow);
}

export async function setReportScheduleEnabled(id: string, enabled: boolean): Promise<{ error: string | null }> {
  const db = getTypedSupabaseClient();
  const { error } = await db.from('report_schedules').update({ enabled, updated_at: new Date().toISOString() }).eq('id', id);
  return { error: error?.message ?? null };
}

export async function deleteReportSchedule(id: string): Promise<{ error: string | null }> {
  const db = getTypedSupabaseClient();
  const { error } = await db.from('report_schedules').delete().eq('id', id);
  return { error: error?.message ?? null };
}
